import {Context, createContext, useContext} from "react";
import {
    isRuntimeController,
    Location,
    Runtime,
    RuntimeActivator,
    RuntimeController,
    RuntimeEnvironment,
    RuntimeError,
    RuntimeEventListener
} from "@common-core/runtime-js/runtime";

export interface RuntimeContextValue<M, E> {
  activator: RuntimeActivator<M, E>;
  version: number;
}

export const RuntimeContext = createContext<RuntimeContextValue<any, any>>({
  activator: {
      activate(
          from: Runtime<any> | string | Location
      ): RuntimeEnvironment<any, any> {
          return {} as RuntimeEnvironment<any, any>;
      },
      activated(): boolean {
          return false;
      },
      current(): RuntimeEnvironment<any, any> {
          return {} as RuntimeEnvironment<any, any>;
      },
      onActivation(listener: RuntimeEventListener<any, any>) {
          return this;
      },
      remove(listener: RuntimeEventListener<any, any>) {
          return this;
      }
  },
  version: 0
});

export function useRuntimeActivator<M, E>(): RuntimeActivator<M, E> {
  const {activator} = useContext<RuntimeContextValue<M, E>>(
    RuntimeContext as unknown as Context<RuntimeContextValue<M, E>>
  );
  return activator;
}

export function useRuntimeController<M, E>(): RuntimeController<M, E> {
  const activator = useRuntimeActivator<M, E>();
  if (isRuntimeController<M, E>(activator)) {
    return activator;
  }
  throw new RuntimeError(`RuntimeProvider not properly configured`);
}

export function useRuntimeEnvironment<M, E>(): RuntimeEnvironment<M, E> {
  const activator = useRuntimeActivator<M, E>();
  return activator.current();
}

export function useRuntime<M>(): Runtime<M> {
  const {runtime} = useRuntimeEnvironment<M, any>();
  return runtime;
}

export function useEnvironment<E>(): E {
  const {environment} = useRuntimeEnvironment<any, E>();
  return environment;
}
